import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminPayouts } from '../../api/adminClient'
import ConfirmModal from '../../components/admin/ConfirmModal'
import { Loader2, Play, Check, XCircle, RotateCcw } from 'lucide-react'

type PayoutItem = {
  id: string
  company_id: string
  amount: number
  currency: string
  status: string
  reference: string | null
  processed_at: string | null
  failure_reason: string | null
  created_at: string
}

export default function AdminPayouts() {
  const { t } = useTranslation('portal')
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [page, setPage] = useState(0)
  const [triggerCompanyId, setTriggerCompanyId] = useState('')
  const [triggerPeriodStart, setTriggerPeriodStart] = useState('')
  const [triggerPeriodEnd, setTriggerPeriodEnd] = useState('')
  const [triggerReference, setTriggerReference] = useState('')
  const [failReason, setFailReason] = useState('')
  const [failId, setFailId] = useState<string | null>(null)
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payouts', status, companyId, page],
    queryFn: () =>
      adminPayouts
        .list({
          status: status || undefined,
          company_id: companyId || undefined,
          skip: page * limit,
          limit,
        })
        .then((r) => r.data),
  })

  const triggerMutation = useMutation({
    mutationFn: () =>
      adminPayouts.trigger(triggerCompanyId, triggerPeriodStart, triggerPeriodEnd, triggerReference || undefined),
    onSuccess: () => {
      setTriggerCompanyId('')
      setTriggerPeriodStart('')
      setTriggerPeriodEnd('')
      setTriggerReference('')
      queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] })
    },
  })

  const retryMutation = useMutation({
    mutationFn: (id: string) => adminPayouts.retry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  })

  const processMutation = useMutation({
    mutationFn: (id: string) => adminPayouts.process(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  })

  const completeMutation = useMutation({
    mutationFn: (id: string) => adminPayouts.complete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] }),
  })

  const failMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => adminPayouts.fail(id, reason),
    onSuccess: () => {
      setFailId(null)
      setFailReason('')
      queryClient.invalidateQueries({ queryKey: ['admin', 'payouts'] })
    },
  })

  const items = (data?.items ?? []) as PayoutItem[]
  const total = data?.total ?? 0

  const statusLabel = (s: string) => {
    const key = s as keyof typeof statusKeys
    return statusKeys[key] ? t(statusKeys[key]) : s
  }
  const statusKeys: Record<string, string> = {
    scheduled: 'admin.payouts.scheduled',
    processing: 'admin.payouts.processing',
    completed: 'admin.payouts.completed',
    failed: 'admin.payouts.failed',
    cancelled: 'admin.payouts.cancelled',
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.payouts.title')}</h1>

      <div className="rounded-xl bg-white border border-gray-200 p-4 mb-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-3">{t('admin.payouts.manualTrigger')}</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.payouts.companyIdLabel')}</label>
            <input
              type="text"
              value={triggerCompanyId}
              onChange={(e) => setTriggerCompanyId(e.target.value)}
              placeholder={t('admin.payouts.companyIdPlaceholder')}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 min-w-[240px] focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.payouts.periodStart')}</label>
            <input
              type="date"
              value={triggerPeriodStart}
              onChange={(e) => setTriggerPeriodStart(e.target.value)}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.payouts.periodEnd')}</label>
            <input
              type="date"
              value={triggerPeriodEnd}
              onChange={(e) => setTriggerPeriodEnd(e.target.value)}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">{t('admin.payouts.referenceOptional')}</label>
            <input
              type="text"
              value={triggerReference}
              onChange={(e) => setTriggerReference(e.target.value)}
              placeholder={t('admin.payouts.referencePlaceholder')}
              className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400"
            />
          </div>
          <button
            type="button"
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending || !triggerCompanyId || !triggerPeriodStart || !triggerPeriodEnd}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-white font-medium hover:bg-emerald-500 disabled:opacity-50 flex items-center gap-2"
          >
            {triggerMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t('admin.payouts.triggerPayout')}
          </button>
        </div>
        {triggerMutation.isError && (
          <p className="mt-2 text-sm text-red-600">{(triggerMutation.error as Error).message}</p>
        )}
        {triggerMutation.isSuccess && (
          <p className="mt-2 text-sm text-emerald-600">{t('admin.payouts.payoutCreated')}</p>
        )}
      </div>

      <div className="flex gap-4 mb-4">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900"
        >
          <option value="">{t('admin.payouts.allStatuses')}</option>
          <option value="scheduled">{t('admin.payouts.scheduled')}</option>
          <option value="processing">{t('admin.payouts.processing')}</option>
          <option value="completed">{t('admin.payouts.completed')}</option>
          <option value="failed">{t('admin.payouts.failed')}</option>
          <option value="cancelled">{t('admin.payouts.cancelled')}</option>
        </select>
        <input
          type="text"
          placeholder={t('admin.payouts.companyIdFilter')}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          className="rounded-lg bg-white border border-gray-300 px-3 py-2 text-gray-900 w-48 placeholder-gray-400"
        />
      </div>

      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-8 text-gray-500 flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('admin.common.loading')}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 text-sm bg-gray-50">
                <th className="p-3">{t('admin.payments.id')}</th>
                <th className="p-3">{t('admin.companies.company')}</th>
                <th className="p-3">{t('admin.payments.amount')}</th>
                <th className="p-3">{t('admin.common.status')}</th>
                <th className="p-3">{t('admin.payouts.processed')}</th>
                <th className="p-3">{t('admin.common.created')}</th>
                <th className="p-3">{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="p-3 text-gray-600 font-mono text-sm">{p.id.slice(0, 8)}…</td>
                  <td className="p-3 text-gray-700 font-mono text-xs">{p.company_id.slice(0, 8)}…</td>
                  <td className="p-3 text-gray-900 font-medium">{Number(p.amount).toFixed(2)} {p.currency}</td>
                  <td className="p-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${
                        p.status === 'completed'
                          ? 'bg-emerald-100 text-emerald-700'
                          : p.status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {statusLabel(p.status)}
                    </span>
                  </td>
                  <td className="p-3 text-gray-500 text-sm">
                    {p.processed_at ? new Date(p.processed_at).toLocaleString() : '—'}
                  </td>
                  <td className="p-3 text-gray-500 text-sm">{new Date(p.created_at).toLocaleString()}</td>
                  <td className="p-3 flex items-center gap-2 flex-wrap">
                    {p.status === 'scheduled' && (
                      <button
                        type="button"
                        onClick={() => processMutation.mutate(p.id)}
                        disabled={processMutation.isPending}
                        className="rounded bg-amber-600 px-2 py-1 text-xs text-white hover:bg-amber-500 flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" /> {t('admin.payouts.process')}
                      </button>
                    )}
                    {p.status === 'processing' && (
                      <button
                        type="button"
                        onClick={() => completeMutation.mutate(p.id)}
                        disabled={completeMutation.isPending}
                        className="rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-500 flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" /> {t('admin.payouts.complete')}
                      </button>
                    )}
                    {p.status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => retryMutation.mutate(p.id)}
                        disabled={retryMutation.isPending}
                        className="rounded bg-amber-600 px-2 py-1 text-xs text-white hover:bg-amber-500 flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> {t('admin.payouts.retry')}
                      </button>
                    )}
                    {(p.status === 'scheduled' || p.status === 'processing') && (
                      <button
                        type="button"
                        onClick={() => { setFailId(p.id); setFailReason(''); }}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-3 h-3 inline" /> {t('admin.payouts.markFailed')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {total > limit && (
          <div className="p-3 border-t border-gray-200 flex justify-between items-center bg-gray-50">
            <span className="text-gray-500 text-sm">{t('admin.common.total')}: {total}</span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('admin.common.previous')}
              </button>
              <button
                type="button"
                disabled={(page + 1) * limit >= total}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50 hover:bg-gray-50"
              >
                {t('admin.common.next')}
              </button>
            </div>
          </div>
        )}
      </div>
      <ConfirmModal
        open={!!failId}
        title={t('admin.payouts.markFailedTitle')}
        variant="danger"
        message={
          failId ? (
            <>
              <p className="mb-2">{t('admin.payouts.markFailedMessage')}</p>
              <input
                type="text"
                placeholder={t('admin.payouts.failureReasonPlaceholder')}
                value={failReason}
                onChange={(e) => setFailReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
              />
            </>
          ) : null
        }
        confirmLabel={t('admin.payouts.markFailed')}
        cancelLabel={t('admin.common.cancel')}
        onConfirm={() => failId && failMutation.mutate({ id: failId, reason: failReason || undefined })}
        onCancel={() => { setFailId(null); setFailReason(''); }}
        loading={failMutation.isPending}
      />
    </div>
  )
}
